// flexg 레퍼런스 팔레트 (2026-09-17 KST 추가)
//
// macaw 의 ThemeProvider 는 커스텀 테마를 받는 prop 이 없어서(defaultTheme 문자열만 받는다)
// 색을 바꾸려면 macaw 가 내보내는 themes 객체를 직접 갱신하는 수밖에 없다.
// 다행히 exports.themes 는 ThemeProvider 가 실제로 읽는 바로 그 객체라, 여기만 바꾸면
//
//   1) ThemeProvider 가 document.documentElement 에 --mu-* 인라인 변수로 심어 준다  → DOM 컴포넌트
//   2) useTheme().themeValues 가 같은 객체를 돌려준다                              → JS 로 색을 읽는 곳
//
// 두 경로가 한꺼번에 맞는다. 2) 가 특히 중요한데, Datagrid 는 canvas 에 그리기 때문에
// CSS 변수가 닿지 않고 useDatagridTheme 이 themeValues 에서 색을 꺼내 쓴다.
// CSS 만 바꿨다면 상품·주문 목록의 표만 옛 색으로 남았을 것이다.
//
// defaultDark 는 건드리지 않는다. 다크 모드는 macaw 기본값을 그대로 쓴다.
//
// 값의 출처는 reference/flexg/DESIGN-TOKENS.md. flexg CSS 에는 디자인 토큰이 없어서
// 선언 빈도 상위값으로 팔레트를 재구성했다.
import { themes } from "@saleor/macaw-ui-next";
import { type CSSProperties } from "react";

// flexg 원본 hex 를 macaw 표기(hsla)로 옮긴 것. 주석의 hex 가 원본이다.
const green = "hsla(118, 67%, 44%, 1)"; // #29bc25 강조 — 배경 30회 + 테두리 42회로 최다
const greenLight = "hsla(127, 54%, 58%, 1)"; // #5acd67 강조 테두리
const greenDark = "hsla(159, 95%, 22%, 1)"; // #036c48 주 버튼 btn_GB_03
const greenBright = "hsla(127, 59%, 48%, 1)"; // #32c243 링크·강조 .point4
const red = "hsla(0, 59%, 49%, 1)"; // #c83434 위험 버튼 btn_RS_a2
const redBright = "hsla(0, 100%, 59%, 1)"; // #ff2f2f 경고 텍스트·배지
const teal = "hsla(183, 42%, 41%, 1)"; // #3c8f94 정보
const amber = "hsla(39, 90%, 37%, 1)"; // #b4790a 경고 btn_OS_a2 테두리

// 97% 는 #f7f7f7 로 반올림되어 주석과 1 어긋났다. 표 머리글 색 기준이라 정확히 맞춘다
// (2026-09-18 KST 수정).
const gray97 = "hsla(0, 0%, 97.3%, 1)"; // #f8f8f8 보조 배경 (배경 2위) — 표 머리글 공통 배경
const gray91 = "hsla(0, 0%, 91%, 1)"; // #e8e8e8 기본 테두리 (테두리 1위, 208회)
const gray84 = "hsla(0, 0%, 84%, 1)"; // #d7d6d6 입력 테두리
const gray46 = "hsla(0, 0%, 46%, 1)"; // #757575 보조 텍스트 — flexg 본문 실측값
const gray37 = "hsla(0, 0%, 37%, 1)"; // #5e5e5e 본문 — flexg 폼 라벨 실측값

/**
 * 본문 글꼴. flexg 의 `body, input, textarea` 선언과 같은 값이다. (2026-09-17 KST 추가)
 *
 * macaw 가 번들하는 Inter 에는 한글 글리프가 없어서, 그대로 두면 한글만 OS 기본 글꼴로
 * 대체되어 레퍼런스보다 크고 가늘게 찍힌다. 웹폰트는 src/index.html 에서 받는다.
 *
 * 글꼴을 정하는 자리가 셋이고 서로 경로가 달라 한 군데로 못 모은다.
 *   - macaw-next: src/index.css 의 html 규칙 (CSS 라 이 상수를 못 읽는다. 바꾸면 같이 고칠 것)
 *   - MUI v4:     src/themeOverrides.ts
 *   - Datagrid:   canvas 로 그려서 CSS 가 닿지 않는다
 */
// 2026-10-19 KST: Windows 는 글자를 108% 로 키운 대시보드 전용 Pretendard 를 쓴다.
// 나눔고딕은 받침 글자 자모가 붙어 보이고, 맑은 고딕은 '+' 가 한글보다 처져 보였다.
// 판별 조건은 src/index.html 의 os-windows 스크립트, 글꼴 목록은 src/index.css 와 같게 둘 것.
const isWindows = typeof navigator !== "undefined" && /Windows/.test(navigator.userAgent);

export const flexgFontFamily = isWindows
  ? `"Pretendard Dashboard", "Malgun Gothic", "맑은 고딕", sans-serif`
  : `"Nanum Gothic", "Apple SD Gothic Neo", "Malgun Gothic", sans-serif`;

/**
 * 상단 가로 메뉴바 색. (2026-09-17 KST 추가)
 *
 * macaw 에는 어두운 내비게이션에 해당하는 색 슬롯이 없어서 토큰으로 넣지 못하고 따로 뺐다.
 * flexg 의 .headpartialwrap / .gnb li a 규칙에서 그대로 가져온 값이다.
 */
export const topBarColors = {
  background: "#25282a",
  item: "#b8b8b8",
  itemActive: "#ffffff",
  activeBorder: "#32c243",
};

/**
 * 어두운 내비게이션 영역(상단바·사이드바) 안에서만 쓰는 macaw 변수 재선언. (2026-09-17 KST 추가)
 *
 * 사이드바를 어둡게 하려고 내부 컴포넌트의 색을 하나씩 고치면, 메뉴·단축키·사용자 메뉴·
 * 배너까지 손댈 곳이 계속 늘고 hover/active 상태를 빠뜨리기 쉽다. 대신 macaw 변수를
 * 이 영역에만 다시 선언한다. CSS 변수는 상속되니 안쪽 컴포넌트가 알아서 어두운 값을 읽고,
 * 상태 색도 전부 변수로 표현돼 있어 같이 따라온다.
 *
 * macaw 가 변수를 <html> 에 인라인으로 심지만, 여기는 그 하위 엘리먼트에 다시 선언하는
 * 것이라 !important 없이도 이 subtree 안에서는 이쪽이 이긴다.
 *
 * hover/pressed 를 흰색 반투명으로 둔 것은 macaw 기본 다크 테마가 쓰는 방식 그대로다.
 */
export const darkNavStyle = {
  "--mu-colors-background-default1": topBarColors.background,
  "--mu-colors-background-default2": topBarColors.background,
  "--mu-colors-background-default3": "#2e3234",
  "--mu-colors-background-default1-hovered": "hsla(0, 0%, 100%, 0.06)",
  "--mu-colors-background-default1-focused": "hsla(0, 0%, 100%, 0.06)",
  "--mu-colors-background-default1-pressed": "hsla(0, 0%, 100%, 0.12)",
  "--mu-colors-background-default-disabled": "hsla(0, 0%, 100%, 0.04)",
  "--mu-colors-text-default1": topBarColors.item,
  "--mu-colors-text-default2": "#929292",
  "--mu-colors-text-default-disabled": "#6b6f72",
  // 강조 텍스트는 본문용 진초록(#036c48)이 어두운 바탕에서 묻힌다. 밝은 초록으로 바꾼다.
  "--mu-colors-text-accent1": topBarColors.activeBorder,
  "--mu-colors-border-default1": "#3b4043",
  "--mu-colors-border-default1-hovered": "#3b4043",
  "--mu-colors-border-default1-focused": "#4a5053",
  "--mu-colors-border-default2": "#3b4043",
  "--mu-colors-border-default-disabled": "#34383b",
} as CSSProperties;

/**
 * 사이드바 리퀴드 글래스 시험. 아래 세 벌이 한 묶음이다. (2026-09-21 KST 추가)
 *
 * 되돌릴 때는 AppLayout 의 사이드바를 darkNavStyle 한 벌로 되돌리고 여기 셋을 지우면 된다.
 *
 * 글자·테두리 색은 darkNavStyle 과 같고 배경만 비운다. 사이드바 안쪽 Box 들이
 * default1/default2 로 제 배경을 칠하고 있어서, 그대로 두면 불투명한 판이 유리를 덮는다.
 * 실제 색과 흐림은 바깥의 glassPaneStyle 이 혼자 맡는다.
 */
export const glassNavStyle = {
  ...darkNavStyle,
  "--mu-colors-background-default1": "transparent",
  "--mu-colors-background-default2": "transparent",
  "--mu-colors-background-default3": "hsla(0, 0%, 100%, 0.1)",
} as CSSProperties;

/**
 * 유리 뒤에 비칠 바탕.
 *
 * 사이드바와 상단 메뉴바는 제 격자 칸에 있어서 뒤로 지나가는 콘텐츠가 없다. 그대로 흐리면
 * 균일한 색을 흐리는 셈이라 아무 변화가 없다. 그래서 흐릴 결을 일부러 깔아 준다.
 *
 * backgroundAttachment 가 fixed 인 것이 핵심이다. 사이드바와 상단바는 서로 다른 격자 칸에
 * 있는 별개 요소라, 기본값(scroll)이면 각자 제 상자를 기준으로 그라디언트를 그려서 둘이
 * 만나는 모서리에서 무늬가 끊긴다. viewport 기준으로 그리면 두 장이 한 장처럼 이어진다.
 */
export const glassBackdropStyle = {
  backgroundColor: "#1b1f22",
  backgroundImage: [
    "radial-gradient(120% 60% at 0% 0%, hsla(28, 90%, 55%, 0.45), transparent 60%)",
    "radial-gradient(100% 50% at 100% 25%, hsla(205, 80%, 55%, 0.28), transparent 60%)",
    "radial-gradient(120% 55% at 10% 100%, hsla(130, 70%, 45%, 0.4), transparent 60%)",
  ].join(", "),
  backgroundAttachment: "fixed",
} as CSSProperties;

/**
 * 어두운 반투명 판. 사이드바와 상단 메뉴바가 쓴다.
 *
 * 흐리기(blur 28px saturate 180%)를 걷어냈다. (2026-09-23 KST 수정)
 * 면은 반투명으로 남겨 두었으므로 뒤의 glassBackdropStyle 결이 흐려지지 않고 그대로
 * 비친다. 결이 지저분해 보이면 다음 둘 중 하나다. 알파를 0.55 보다 올려 결을 눌러
 * 앉히거나, glassBackdropStyle 의 backgroundImage 를 빼서 단색 바탕으로 되돌린다.
 */
export const glassPaneStyle = {
  backgroundColor: "hsla(210, 14%, 13%, 0.55)",
} as CSSProperties;

/**
 * 밝은 유리판. 모달·저장 바처럼 흰 콘텐츠 위에 뜨는 것에 쓴다.
 *
 * 알파를 0.8 로 높게 잡은 것은 이 위에 본문 글자가 올라가기 때문이다. 더 비치게 하면
 * 뒤에 무엇이 지나가느냐에 따라 대비가 들쭉날쭉해진다.
 */
export const glassLightPaneStyle = {
  backgroundColor: "hsla(0, 0%, 100%, 0.8)",
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
} as CSSProperties;

/**
 * 유리를 입힌 페이지의 바탕 (2026-09-21 KST 추가)
 *
 * 사이드바용 glassBackdropStyle 은 어둡다. 본문 글자가 짙은 회색이라 그 위에 얹으면
 * 읽을 수 없어서, 같은 세 색조를 밝은 바탕에 옅게 깐 것을 따로 둔다.
 *
 * 함께 내려보내는 default1 이 이 화면의 유리를 만든다. 카드와 타일은 대부분
 * backgroundColor="default1" 로 흰 배경을 칠하는데, 이 나무 밑에서만 그 값을 반투명으로
 * 바꾸면 카드를 하나하나 고치지 않아도 전부 비친다. 겹쳐 놓인 면은 한 겹씩 밝아지므로
 * 따로 선을 긋지 않아도 안쪽 판으로 구분된다.
 *
 * background-attachment 는 사이드바와 같게 fixed 로 둔다. 스크롤해도 결이 따라다니지 않아야
 * 유리 뒤에 실제 공간이 있는 것처럼 보인다.
 */
export const glassPageBackdropStyle = {
  backgroundColor: "#eef0f3",
  backgroundImage: [
    "radial-gradient(120% 60% at 0% 0%, hsla(28, 95%, 62%, 0.3), transparent 60%)",
    "radial-gradient(100% 50% at 100% 20%, hsla(205, 90%, 60%, 0.24), transparent 60%)",
    "radial-gradient(120% 55% at 15% 100%, hsla(130, 70%, 50%, 0.28), transparent 60%)",
  ].join(", "),
  backgroundAttachment: "fixed",
  "--mu-colors-background-default1": "hsla(0, 0%, 100%, 0.62)",
} as CSSProperties;

/**
 * 유리판 한 장.
 *
 * 면 색은 위 바탕이 내려보내는 default1 이 칠하므로 여기서는 흐리기와 가장자리만 맡는다.
 * 테두리와 그림자를 같이 주는 건, 판의 끝이 보여야 떠 있는 것으로 읽히기 때문이다.
 * 모서리 둥글기는 쓰는 쪽마다 달라서 여기서 정하지 않는다.
 */
export const glassPanelStyle = {
  backdropFilter: "blur(18px) saturate(160%)",
  WebkitBackdropFilter: "blur(18px) saturate(160%)",
  border: "1px solid hsla(0, 0%, 100%, 0.55)",
  boxShadow: "0 8px 24px hsla(212, 20%, 30%, 0.12)",
} as CSSProperties;

/** 홈 카드. */
export const glassHomeCardStyle = {
  ...glassPanelStyle,
  borderRadius: "12px",
  // DashboardCard.Content 는 좌우 패딩만 준다. 테두리가 없던 동안은 문제가 없었지만
  // 판의 가장자리가 생긴 지금은 내용이 아래 선에 붙어서 여기서 채워 준다.
  paddingBottom: "16px",
} as CSSProperties;

/**
 * 검색 상자의 테두리와 그림자. flexg 의 .BigBox_basic 을 그대로 옮긴 것. (2026-09-18 KST 추가)
 *
 * 세 값 모두 macaw 토큰에 없어서 원시 prop 으로 준다.
 *   반지름 10px   토큰은 8px(4) 과 12px(5) 뿐이라 그 사이 값이 없다
 *   테두리 #d8d8d8 전역 default1(#e8e8e8)보다 진하다
 *   그림자        shadow 토큰은 전부 아래로 떨어지는 형태라 사방으로 퍼지는 이 모양이 없다
 *
 * 쓰는 곳이 일곱이다. 주문·상품·구매후기·공급사 검색 패널과 무통장입금 내역·매출 통계·
 * 상품별 매출 페이지의 검색 상자. 안쪽 여백은 자리마다 달라 여기 넣지 않는다.
 */
export const searchBoxStyle = {
  // 목록 화면의 유리를 걷으면서 반투명·흐리기를 빼고 불투명 흰 면으로 되돌린다.
  // (2026-09-23 KST 수정) 테두리와 그림자는 flexg .BigBox_basic 그대로 둔다.
  backgroundColor: "default1",
  borderStyle: "solid",
  borderWidth: 1,
  __borderColor: "#d8d8d8",
  __borderRadius: "10px",
  __boxShadow: "0 0 10px rgba(0, 0, 0, 0.18)",
} as const;

/**
 * 검색 영역 왼쪽 항목 라벨 (2026-09-18 KST 추가).
 *
 * flexg 는 라벨 칸을 표 머리글처럼 다루어 #f8f8f8(= default2) 을 깔고 글자를 굵게 쓴다.
 * 행마다 높이가 달라서 세로로 늘려야 글자만 감싼 조각이 아니라 칸으로 보인다.
 * macaw 의 alignSelf 에 stretch 가 없는데, flex 자식에서 normal 은 stretch 로 동작한다.
 * 가로 폭은 자리마다 달라 여기 넣지 않는다.
 */
export const searchLabelStyle = {
  alignSelf: "normal",
  display: "flex",
  alignItems: "center",
  flexShrink: "0",
  paddingX: 2,
  paddingY: 1,
  backgroundColor: "default2",
  borderRadius: 2,
} as const;

const { colors, fontSize, fontWeight } = themes.defaultLight;

export const applyFlexgPalette = (): void => {
  // 기준 글자 크기를 flexg 와 같은 12px 로 내린다. (2026-09-17 KST 추가)
  //
  // 스케일(11·12·13·14·16·18·20·22·24·28·34px) 자체는 flexg 가 쓰는 값을 이미 다 품고 있어
  // 손대지 않는다. 본문이 집는 3번만 12px 로 내리면 된다. 2번과 값이 같아지지만, 둘을
  // 갈라 쓰던 자리(예: 표 머리글은 2번)는 아래 굵기로 구분되므로 위계는 남는다.
  //
  // rem 을 쓰는 레거시 macaw·MUI 는 이 토큰과 무관하다. src/index.css 의 루트 크기로 맞춘다.
  Object.assign(fontSize, { 3: "12px" });

  // Nanum Gothic 은 굵기가 400 과 700 둘뿐이다. CSS 글꼴 매칭 규칙상 500(medium)은 400 으로
  // 내려붙어 본문과 구분이 사라지고, 600(bold)은 700 으로 올라붙는다. 그대로 두면 카드 제목과
  // 표 머리글이 본문과 같은 굵기로 보인다. 실제로 찍히는 값을 토큰에 그대로 적는다.
  // 되돌리려면 이 줄만 지우면 macaw 기본값(500/600)으로 돌아간다. (2026-09-17 KST 추가)
  // Windows 는 굵기를 자유롭게 고를 수 있는 Pretendard 를 써서, 700 은 너무 두꺼워 600 으로
  // 낮춘다. (2026-10-19 KST 수정)
  Object.assign(
    fontWeight,
    isWindows ? { medium: "600", bold: "600" } : { medium: "700", bold: "700" },
  );

  Object.assign(colors.background, {
    // 중립 — default1(흰색)은 flexg 와 같아서 그대로 둔다
    default2: gray97,
    default3: gray91,

    // 강조
    accent1: green,
    accent1Hovered: "hsla(118, 67%, 44%, 0.12)",
    accent1Focused: "hsla(118, 67%, 44%, 0.32)",
    accent1Pressed: "hsla(118, 67%, 44%, 0.24)",

    // 버튼 — flexg 는 초록이 주 액션이 아니다. (2026-09-17 KST 수정)
    //
    // flexg 의 주문 검색 화면을 보면 상품 찾기·적용·SMS발송·공급사 변경 같은 일상 액션이
    // 전부 슬레이트(#7b858d, btn_GS_7b)이고, 폼을 제출하는 '검색'만 한 단계 진한
    // 슬레이트(#596269, btn_GB_59)다. 진초록(#036c48)은 '주문자 연락처 ↓' 처럼 내려받기
    // 계열에만 드물게 쓰인다. 종전에는 그 반대로 잡아 두었던 것을 레퍼런스대로 뒤집는다.
    //
    // macaw 는 변형이 넷(primary·secondary·tertiary·error)뿐이라 flexg 의 용도별 다섯 색을
    // 그대로 옮기지 못한다. 수가 가장 많은 쪽에 맞춰 secondary(244개)를 일상 액션 색으로,
    // primary(149개)를 검색 색으로 둔다. 진초록은 이제 버튼에 안 쓰이고 성공 상태색으로만 남는다.
    //
    // 버튼이 거의 다 회색이라 화면이 단조로워 AdminLTE(Bootstrap 4) 버튼 색으로 바꾼다.
    // 확정(primary)은 btn-success, 일반(secondary)은 btn-secondary, 위험(error)은 btn-danger.
    // hover·focus·pressed 도 Bootstrap 처럼 한 단계씩 진해진다. (2026-09-30 KST 수정)
    //
    // 확정 버튼을 진초록(#036c48)으로 바꾼다. 주문 진행 상태 카드의 '배송준비 처리' 같은
    // 상태 변경 버튼과 검색 폼의 '검색'은 초록(#28a745)으로 따로 칠해 구분한다. 그쪽은
    // src/index.css 의 [data-order-status-actions]·[data-search-button] 이 칠한다.
    // (2026-09-30 KST 수정) '검색'은 진회색(#495057)으로 바꿨다 (2026-10-05 KST 수정)
    buttonDefaultPrimary: "#036c48",
    buttonDefaultPrimaryHovered: "#025a3c",
    buttonDefaultPrimaryFocused: "#025a3c",
    buttonDefaultPrimaryPressed: "#024d33",

    // 보조 버튼 — 흰 바탕에서 회색 채움으로 바뀐다. 테두리와 입체감은 src/index.css 에서 준다.
    buttonDefaultSecondary: "#6c757d",
    buttonDefaultSecondaryHovered: "#5a6268",
    buttonDefaultSecondaryFocused: "#5a6268",
    buttonDefaultSecondaryPressed: "#545b62",

    // 위험 버튼 — 삭제류 공통. 테두리(#a11212)는 src/index.css 에서 준다.
    // hover·focus·pressed 는 기본색에서 한 단계씩 진하게. (2026-10-08 KST 수정)
    buttonCriticalPrimary: "#c83434",
    buttonCriticalPrimaryHovered: "#b32e2e",
    buttonCriticalPrimaryFocused: "#b32e2e",
    buttonCriticalPrimaryPressed: "#a12929",

    // 상태 배경
    critical2: redBright,
    success1: "hsla(127, 52%, 81%, 1)", // #b7e8bd
    warning1: "hsla(39, 92%, 84%, 1)", // #fce2b2
    info1: "hsla(181, 60%, 90%, 1)", // #31c0c5 계열 연한 청록
  });

  Object.assign(colors.border, {
    default1: gray91,
    default1Hovered: gray91,
    default1Focused: gray84,
    default2: gray84,
    accent1: greenLight,
    critical1: red,
    success1: greenDark,
    warning1: amber,
    info1: teal,
  });

  Object.assign(colors.text, {
    // 글자색은 flexg 의 CSS 선언이 아니라 화면 실측값을 따른다. (2026-09-18 KST 수정)
    //
    // 처음에는 flexg 의 body 선언(#727272)과 두 번째로 많이 쓰인 색(#929292)을 그대로
    // 옮겼는데, 막상 같은 화면을 나란히 놓고 재보니 우리 쪽 글자가 눈에 띄게 흐렸다.
    // flexg 가 body 선언을 곳곳에서 더 진한 색으로 덮고 있기 때문이다. 캡처 두 장의 축척을
    // 초기화·검색 버튼 높이(flexg 32px, 이쪽 29px)로 맞춘 뒤 잰 값은 이렇다.
    //
    //              flexg      종전 이쪽
    //   폼 라벨     #606060    #797979
    //   체크박스    #757575    #878787
    //   라벨 크기   12.5px     12.4px   ← 크기는 차이가 없다. 색만 문제였다
    //
    // 축소된 캡처라 획이 흰 배경과 평균되어 실제보다 밝게 찍힌다. 즉 flexg 의 진짜 색은
    // 위 값보다 더 진하다. 그래서 실측값을 그대로 토큰에 넣는다.
    //
    // 종전 #929292 는 명도비가 3.2:1 로 WCAG AA(4.5:1)에 못 미쳤다. 게다가 macaw 는
    // default2 를 폼 라벨에 쓰는데, 그러면 라벨이 본문보다 연해진다. flexg 는 반대로
    // 라벨을 본문보다 진하게 쓴다. 아래 값은 그 순서를 지키면서 둘 다 AA 를 넘긴다.
    default1: gray37, // #5e5e5e · 6.4:1
    default2: gray46, // #757575 · 4.6:1

    // 링크·강조. flexg 의 .point4(color:#32c243; font-weight:bold)를 그대로 따른다.
    // 흰 배경 대비 명도비가 2.35:1 로 WCAG AA(4.5:1)에 한참 못 미치니, 색 하나로만 뜻을
    // 구분하는 자리에는 쓰지 말 것. flexg 처럼 굵기를 함께 줘서 형태로도 구분되게 한다.
    // 되돌리려면 greenDark(#036c48, 6.5:1)로 바꾼다. (2026-09-17 KST 수정)
    accent1: greenBright,

    // secondary 버튼이 슬레이트로 채워지면서 글자는 흰색이 된다(flexg btn_GS_7b 의 color:#fff).
    // macaw 기본값은 본문과 무관한 별도 토큰이라 거의 검정(#242726)이었다. (2026-09-17 KST 수정)
    buttonDefaultSecondary: "hsla(0, 0%, 100%, 1)",
    critical1: "hsla(0, 59%, 35%, 1)",
    critical2: red,
    success1: greenDark,
    warning1: amber,
    info1: teal,
  });
};
